import {kbUserDataLoaded, parseJSONTool, toYAML} from "../utils";
import {
    generateMsgId,
    limitedChatMessages,
    msgIdToTimestamp,
    parseChatId,
    recentReactions,
    transformReactions
} from "./utils";
import {deepClone, parseJSON, publicChatData} from "../App/utils";
import {handleCreateChat} from "./handleCreateChat";
import {
    chatAddMessages,
    getChatMessages,
    getChatMessagesCache,
    handleData,
    requestChatStream
} from "../API/chatAPI";
import {defaultImageEditorInstructions} from "../Image/defaultInstructions";
import {decryptKBItem, getKBKey} from "../App/cryptoUtils";
import {ChatModels} from "../constants";
import {blobUrlToFilename, getRequestId} from "../Image/helpers";
import {createOnImageToTextResponse, getKBImageURL} from "../InputTools/ImageToText";
import {wsSend} from "../API/Websocket";
import {WS_MESSAGE} from "../App/constants";
import {playSound} from "../Media/utils";
import {fetchContextItems} from "./ContextItemsUtils";

export const createRequestChatAPI = ({ KBData,
    setIsStreaming, setMessages, setSystemAlert, abortController, isImageEditor, kbId, KB, navigateToChat,
    setKBChats, itemsAsSearchResults, imageFiles, setBlockingLoading, setImageFile,
    contextWindow, setTotalTokensSum, setContextItems, renderSettings
}) => {
    const RequestChatAPI = (nextMessages, {nextImageFiles} = {}) => new Promise(async (resolve, reject) => {
        setIsStreaming(true);
        // Create a new AbortController for the new request
        if (abortController.current) abortController.current.abort();
        abortController.current = new AbortController();

        const chatMessages = [...nextMessages];
        setMessages([...chatMessages]);

        const limitedMessages = limitedChatMessages(chatMessages, KB);

        if (limitedMessages.length !== chatMessages.length) {
            setSystemAlert({msg: 'Chat exceeds AI model\'s token limit!', type: 'error', duration: 3000})
        }

        const lastMessage = chatMessages[chatMessages.length - 1];

        const signal = abortController.current?.signal;
        let chatId = parseChatId();
        if (!chatId && chatMessages?.length === 1) {
            /**
             * User Create chat
             */
            // console.log('User Create chat Add Message', deepClone(lastMessage))
            handleCreateChat({chatMessages: [deepClone(lastMessage)], navigateToChat, setKBChats})
        } else if (chatId && lastMessage?.role === 'user') {
            /**
             * User Add Message
             */
            // console.log('User Add Message', deepClone(lastMessage))
            chatAddMessages(chatId, [deepClone(lastMessage)]).then()
        }
        let messagesToSend = deepClone(limitedMessages);
        // process image editor images
        if (isImageEditor) {
            const newLimitedMessage = deepClone(limitedMessages)
            let editorState = newLimitedMessage[0]?.content && parseJSON(newLimitedMessage[0]?.content)

            // replace url-s
            editorState.images = editorState.images.map(o => {
                const imageUrl = o?.url;
                return {
                    ...o,
                    url: imageUrl?.startsWith?.('blob') ? getKBImageURL(blobUrlToFilename(imageUrl), kbId) : imageUrl
                }
            })

            let visualImagesArrayContent = [];

            if (ChatModels[KB?.model]?.vision?.type === 'image_url') {
                visualImagesArrayContent.push({type: 'text', text: `Below listed all images, first JSON metadata and second is the image_url`})

                editorState?.images?.forEach(image => {
                    if (image?.visibleToAI || image?.selected) {
                        visualImagesArrayContent.push({type: 'text', text: JSON.stringify(image)})
                        visualImagesArrayContent.push({type: 'image_url', image_url: { url: image?.url }})
                    }
                })
            } else {
                delete editorState.images
            }

            newLimitedMessage[0].content = JSON.stringify(editorState)
            const imageEditorTool = parseJSONTool(KB?.inputTools, 'imageEditor');

            if (visualImagesArrayContent?.length) {
                messagesToSend = [
                    ...(!KB?.kbInstructions ? [
                        {
                            role: 'system',
                            content: defaultImageEditorInstructions(imageEditorTool)
                        }
                    ] : []),
                    // Images are only allowed for the user role
                    { role: 'user', content: JSON.stringify(visualImagesArrayContent) },
                    ...newLimitedMessage
                ];
            } else {

                messagesToSend = [
                    ...(!KB?.kbInstructions ? [
                        {
                            role: 'system',
                            content: defaultImageEditorInstructions(imageEditorTool)
                        }
                    ] : []),
                    ...newLimitedMessage
                ];
            }

        }

        const params = {
            chatPayload: {
                "model": KB.model,
                "messages": messagesToSend,
                "temperature": 0,
                "stream": true,
            },
            AESKey: getKBKey(kbId),
            OpenAIKey: KB.OpenAIAPIKey
        };

        // const totalTokens = itemsAsSearchResults?.reduce((acc, o) => acc + (o._source.totalTokens || 0), 0);

        // if (totalTokens < ChatModels?.[KB.model]?.searchLimit) {
        //     params.injectedItems = itemsAsSearchResults;
        // }

        if (contextWindow && !renderSettings?.disableContextItems) {
            const {totalTokens, items} = await fetchContextItems({inputValue: '', messages: chatMessages, KB, contextWindow})
            if (items) {
                params.instructionSuffix = items.map(item => toYAML(item.item)).join('\n\n###\n\n');
                setTotalTokensSum(totalTokens)
                setContextItems(items)
            }
        }

        if (renderSettings?.instructionSuffix) {
            params.instructionSuffix = renderSettings?.instructionSuffix;
        }

        chatId = parseChatId();
        if (chatId) params.chatId = chatId

        const response = await requestChatStream(params, signal, KBData);

        // stopped
        if (!response?.body) return null;

        setMessages([...chatMessages]);

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let parts = [];
        const requestId = getRequestId();

        let incompleteMessage = ''
        reader.read().then(async function processText({ done, value }) {
            const chatId = parseChatId();
            let lastMsg = chatMessages[chatMessages.length-1];
            if (done) {
                setIsStreaming(false);
                if (isImageEditor) return;

                /**
                 * Assistant Add Message (Assistant Done)
                 */
                if (chatId && lastMsg?.role === 'assistant') {
                    chatAddMessages(chatId, [deepClone(lastMsg)]).then()
                }
                return;
            } else {
                setIsStreaming(true);
            }

            const jsonMessages = decoder.decode(value).split('\n');

            if (!incompleteMessage) window.dispatchEvent(new CustomEvent('newTokens', { detail: { tokens: jsonMessages, requestId } }));

            for (let msg of jsonMessages) {
                if (!msg) continue;
                let data = parseJSON(incompleteMessage + msg);
                if (data === undefined) {
                    incompleteMessage += msg;
                    continue;
                } else {
                    incompleteMessage = '';
                }

                await handleData(response, data, chatMessages, parts, nextImageFiles, resolve, setIsStreaming,
                    setSystemAlert, setMessages, RequestChatAPI, onImageToTextResponse, imageFiles, kbId, chatId, KBData
                )
            }
            reader.read().then(processText).catch(() => {
                /**
                 * Stream aborted Add Message
                 */
                const chatId = parseChatId();
                if (chatId && lastMsg.role === 'assistant') {
                    // console.log('Stream aborted Add Message', deepClone(lastMsg))
                    chatAddMessages(chatId, [deepClone(lastMsg)])
                }
                // console.log('Stream aborted');
            });
        });
    }).catch(e => {
        console.log('chatRequestError', e.toString())
        console.error(e);
    });

    const onImageToTextResponse = createOnImageToTextResponse({
        kbId,
        setBlockingLoading,
        imageFiles,
        setImageFile,
        messages: [],
        RequestChatAPI
    });

    return RequestChatAPI;
}

let typingTimeoutId;
export const createHandleWebsocketMessages = ({chatId, setMessages, setTyping, setUnreadMessages, chatContainerRef}) => {

    return (event) => {
        const data = JSON.parse(event.detail)
        const eventName = data?.eventName
        const table  = data?.tableName
        const requestedChatId = data?.NewImage?.chatId || data?.OldImage?.chatId
        let userId = data?.NewImage?.userId
        const userIdWithoutPrefix = userId?.split('_')?.[1]
        const isMe = userIdWithoutPrefix ? userIdWithoutPrefix === kbUserDataLoaded?.myUserId : undefined

        /**
         * Typing
         */
        if (data?.type === WS_MESSAGE.USER_TYPING && chatId && chatId === data?.chatId) {
            if (typingTimeoutId) clearTimeout(typingTimeoutId);

            playSound('/static/sound/popclick.mp3', 0.2)
            setTyping({role: 'user', userId: `User_${data?.userId}`, type: WS_MESSAGE.USER_TYPING, content: '...', msgId: generateMsgId()})
            typingTimeoutId = setTimeout(() => {
                setTyping({});
            }, 8000);
        }

        else if (table === "openkb-chat-messages") {

            if (publicChatData) {
                getChatMessages({ chatId }).then(data => {
                    if (data?.data?.messages) {
                        setMessages(data.data.messages);
                    }
                })
                return;
            }

            /**
             * Hidden Chat message
             */
            if (requestedChatId !== chatId && eventName === "INSERT" && table === "openkb-chat-messages" && !isMe) {
                setUnreadMessages(prev => ({ ...prev, [requestedChatId]: (prev?.[requestedChatId] || 0) + 1 }));
                return;
            }

            /**
             * Current Chat
             */
            if (eventName === 'INSERT' && data?.NewImage && chatId && requestedChatId === chatId) {
                setTyping({});

                const {content, msgId, role, reactions} = data?.NewImage;
                setMessages(prev => {
                    const msgExists = prev.some(message => message.msgId === msgId);

                    if (!msgExists) {
                        // Apply transformations to content and reactions
                        const decryptedContent = decryptKBItem(content);
                        const transformedReactions = transformReactions(reactions);

                        return [...prev, {...data?.NewImage, content: decryptedContent, reactions: transformedReactions}];
                    } else {
                        // Update the last message to include a 'sent: true' flag
                        return prev.map((message, index, array) => {
                            if (index === array.length - 1) {
                                return {...message, sent: +new Date()};
                            }
                            return message;
                        });
                    }
                });

            } else if (eventName === 'MODIFY' && data?.NewImage) {
                const oldReactions = data?.OldImage?.reactions;
                const {msgId, reactions} = data?.NewImage;

                let diffReactions = oldReactions
                    ?.filter(x => !reactions?.includes(x))
                    ?.concat(reactions?.filter(x => !oldReactions?.includes(x)));

                // ignore changes to local reactions
                if (diffReactions?.some(o => recentReactions?.[msgId]?.has(o))) return;

                const transformedReactions = transformReactions(reactions);
                setMessages(prev => prev.map(message =>
                    message.msgId === msgId
                        ? {
                            ...message,
                            ...data?.NewImage,
                            content: decryptKBItem(data?.NewImage.content),
                            reactions: transformedReactions
                        }
                        : message
                ));
            } else if (eventName === 'REMOVE' && data?.OldImage) {
                const {msgId} = data.OldImage;
                setMessages(prev => prev.filter(message => message.msgId !== msgId));
            } else {
                // console.log(`Should never happen!! eventName ${eventName} `, data);
            }

        }
    }
};

export const onChangeChat = ({chatId, setUnreadMessages, messages, setMessages, chatAbortController,
    setLoading, isImageEditor, setLastEvaluatedKey, lastEvaluatedKey}) => {
    if (chatId) {
        wsSend({type: WS_MESSAGE.SET_CHAT_ID, chatId})
        // chat just created no need to update
        if (messages?.length === 1 && (+new Date - msgIdToTimestamp(messages[0]?.msgId)) < 1000) return

        if (getChatMessagesCache?.[chatId]?.data?.messages) {
            setMessages(getChatMessagesCache[chatId].data.messages);
        }

        if (chatAbortController.current) {
            chatAbortController.current.abort();
        }

        // Create a new AbortController for the new request
        chatAbortController.current = new AbortController();
        const { signal } = chatAbortController.current;

        setLoading(true);
        getChatMessages({chatId, signal}).then(data => {
            if (data?.data?.messages) {
                setMessages(data.data.messages);
                setLastEvaluatedKey(data?.data?.lastEvaluatedKey)

                setUnreadMessages(prev => {
                    const { [chatId]: _, ...rest } = prev;
                    return rest;
                });
            }
            setLoading(false);
        })

    } else {
        if (!isImageEditor) setMessages([])
    }
}